import _ from 'lodash';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import uuid from 'uuid';
import { TextCollectionConfig, DOLPHIN_MISC } from '@scale/shared/defaults';
import {
  TextCollectionField,
  TextCollectionFieldType,
  TextCollectionAttachment,
  TextCollectionRankingOrderOption,
  LogBehaviourActions,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  isUnitFieldAnnotation,
  UnitFieldAnnotation,
  FieldSetAnnotation,
  TextCollectionResponse,
} from '@scale/shared/types/response/textcollection';
import {
  areAllFieldConditionsMet,
  isFieldConditionsCodeMet,
  flattenFields,
  flattenAnnotations,
  areTemplateVariableConditionsMet,
} from '@scale/shared/textcollection/helpers';
import {
  TextCollectionAnnotationsOrCallback,
  TextCollectionGetPreviousResponsesResponse,
} from '@scale/shared/types/textCollection';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { Button, Link } from '@material-ui/core';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import {
  Breadcrumb,
  getCurrentFields,
  getCurrentAnnotations,
  getQuickAddResponses,
  getBreadcrumbTitle,
} from './breadcrumbs';
import { AutoCompleteQuery } from './autoComplete';
import FieldSection from './FieldSection';
import Markdown from '@scale/frontend-lib/markdown';
import { makeStyles } from 'design-system/styles';
import TextInput from 'design-system/TextInput';
import { TextCollectionDispatch } from './reducer';
import { getDeleteMessage } from './delete';
import { useSuggestions } from './suggestions';
import { isFormFields } from '@scale/shared/textcollection/field';
import FormFieldNav from './FormFieldNav';
import { LintDiagnostic, LintSeverity, LintIssue } from '@scale/shared/task_lint/task_linter';
import InlineFormAttachmentView from 'react-scale/components/scalers/tasks/textcollection/InlineFormAttachmentView';
import { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useSequentialNumericShortcuts } from 'react-scale/hooks/useSequentialShortcuts';
import { useFairSettings } from 'react-scale/hooks/rlhf/useFairSettings';
import { useFieldValidation } from 'react-scale/hooks/rlhf/useFieldValidation';
import { useAsync, useToggle } from 'react-use-v13';
import Kbd from 'design-system/Kbd';
import { GenerationButton as DolphinGenerationButton } from './DolphinComponents';
import apiFetch from 'react-scale/lib/api-fetch';
import { useChatbotContext } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import color from 'color';
import TextCollectionDiff from 'react-scale/components/scalers/tasks/textcollection/TextCollectionDiff';
import SAlert from 'react-s-alert';
import GeneratePromptButton from 'react-scale/components/scalers/tasks/textcollection/DolphinComponents/GeneratePromptButton';
import { GENERATED_PROMPT_FIELD_ID } from '@scale/shared/dolphin/DASConstants';
import { RlhfBenchmarkType, ChatHistory } from '@scale/shared/types/rlhf';
import { ChatApiEndpoint, ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';
import { ReviewLevel } from '@scale/shared-types';
import ReactTooltip from 'react-tooltip';
import Latex from 'react-latex';
import { Markdown as MarkdownComponent } from '@scale/scaleui';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';

interface TextCollectionFormProps {
  agreementScores?: any;
  title?: string;
  description?: string;
  breadcrumbs: Breadcrumb[];
  previousBreadcrumbs?: Breadcrumb[];
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
  fields: TextCollectionField[];
  decisionTreeFields?: TextCollectionField[];
  annotations: Record<string, TextCollectionAnnotation>;
  annotationsRef: React.MutableRefObject<Record<string, TextCollectionAnnotation>>;
  setAnnotations: (
    annotations: TextCollectionAnnotationsOrCallback,
    breadcrumbs?: Breadcrumb[],
  ) => void;
  deleteAnnotations: (breadcrumbs: Breadcrumb[]) => void;
  disabled?: boolean;
  blocked?: boolean;
  onRequestDelete: () => void;
  onRequestBack: () => void;
  autoComplete: (query: AutoCompleteQuery) => Record<string, TextCollectionAnnotation>[];
  readOnly: boolean;
  reviewMode: boolean;
  reviewLevel?: ReviewLevel;
  quickAddFieldSets: string[];
  dispatch: TextCollectionDispatch;
  readOnlyFieldIds?: string[];
  blurredFieldIds?: string[];
  bboxFieldToEdit: string | null;
  setBboxFieldToEdit: (fieldId: string | null) => void;
  attachment: TextCollectionAttachment;
  lintErrors?: LintDiagnostic[];
  projectId?: string;
  captcha?: string;
  captchaUserAnswer?: string;
  setCaptchaUserAnswer?: (x: string) => void;
  onFormFieldNavigate?: (finished: boolean) => void;
  inlineAttachments?: TextCollectionAttachment[];
  completedAttachments?: boolean[];
  updateCompletedAttachments?: (completedAttachments: boolean[]) => void;
  isScaleStudio?: boolean;
  assignmentId?: string;
  loggedUserId?: string;
  textCollectionConfig?: TextCollectionConfig;
  isAudit?: boolean;
  rankingOrderOptions?: TextCollectionRankingOrderOption[];
  useCrabHack?: boolean;
  numLeftColumnFields?: number;
  useSplitScreen?: boolean;
  isReview?: boolean;
  isCustomer?: boolean;
  rlhfParams?: RLHFParams;
  isFair?: boolean;
  setCanSubmitAiAssistant?: (isDone: string[]) => void;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  useSimpleCodeField?: boolean;
  omitFormFieldNav?: boolean;
  autoSpec?: boolean;
  autoCritique?: boolean;
  aiAssistant?: boolean;
  logBehaviourActions?: LogBehaviourActions;
  perTurnCheck?: () => any;
  isTestQuestion?: boolean;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  readOnlyMessage?: string;
  templateVariables?: Record<string, string>;
}

export interface RLHFParams {
  numberOfCompletions: number;
  chatApiEndpoint: string;
  chatApiEndpoints?: ChatApiEndpoint[];
  numberOfInteractions: number;
  chatApiAdditionalParams: any;
  setCanSubmitRlhf?: (isDone: boolean) => void;
  perConversationFieldIds: string[];
  perTurnFieldIds?: string[];
  editBestCompletion: boolean;
  chatApiMultiEndpoints?: string[];
  autoSpec?: boolean;
  autoCritique?: boolean;
  showMarkdownViewForEditCompletion?: boolean;
  flamingoPipelineVersion?: string;
  enableMultiIterationsUntilPerfectAnswer?: boolean;
  maxIterationForPerfectAnswer?: number;
  isFair?: boolean;
  useEditedResponseInHistory?: boolean;
  chatBoxTexts: string[];
  rlhfBenchmarkType?: RlhfBenchmarkType;
  maxRankingTies?: number;
  // take precedence over numberOfInteractions
  rangedNumberOfInteractions?: {
    min: number;
    max: number;
  };
}

// eslint-disable-next-line complexity
export default function TextCollectionForm({
  setAnnotations,
  disabled,
  blocked,
  annotations,
  ...props
}: TextCollectionFormProps) {
  const styles = useTextCollectionFormStyles();
  let { annotations: currentAnnotations } = React.useMemo(
    () => getCurrentAnnotations(annotations, props.breadcrumbs),
    [annotations, props.breadcrumbs],
  );

  const {
    fieldValidationEnabled,
    fieldIdsToValidate,
    validateField,
    fieldValidationResults,
    fieldIdPendingValidation,
    setFieldIdPendingValidation,
  } = useFieldValidation(props.projectId, props.assignmentId);

  const chatbotContext = useChatbotContext();
  const llmHelper = useLLMHelper();
  const templateVariables = props.templateVariables ?? useTemplateVariableStore.getState();

  const fairSettings = useFairSettings(!!props.isFair, annotations, props.fields);
  if (fairSettings && fairSettings.filteredFields) {
    props.fields = fairSettings.filteredFields;
  }
  const numLeftColumnFields = props.numLeftColumnFields ?? 1;
  useEffect(() => {
    if (
      !chatbotContext.useChatbotV2 &&
      (chatbotContext.reviewNoBranching || chatbotContext.readOnly)
    ) {
      props.rlhfParams?.setCanSubmitRlhf?.(true);
    } else {
      props.rlhfParams?.setCanSubmitRlhf?.(chatbotContext.isDone);
    }
  }, [chatbotContext.isDone]);

  useEffect(() => {
    props.setCanSubmitAiAssistant?.(llmHelper.responseNotDifferentEnough); // HACK
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.responseNotDifferentEnough]);

  // set the chat history as its own annotation
  useEffect(() => {
    if (!chatbotContext.enabled && !llmHelper.enabled) return;
    const existingAnnotations = getCurrentAnnotations(
      props.annotationsRef.current,
      props.breadcrumbs,
    ).annotations;

    let response = [];

    if (chatbotContext.enabled) response.push(chatbotContext.history);

    response = [
      ...response,
      ...((existingAnnotations.rlhf_history?.response as any[])?.filter(
        (r: any) => (r as ChatHistory).autoCritique?.length,
      ) ?? []),
    ]; // must let history be the first element

    if (chatbotContext.autoSpecText?.length) {
      response.push({ autoSpec: chatbotContext.autoSpecText, interactions: [] });
    }
    if (
      chatbotContext.critiqueText?.length &&
      !_.some(response, (r: any) => (r as ChatHistory).autoCritique === chatbotContext.critiqueText)
    ) {
      response.push({ autoCritique: chatbotContext.critiqueText, interactions: [] });
    }
    if (llmHelper.autoSpecText?.length) {
      response.push({ autoSpec: llmHelper.autoSpecText, interactions: [] });
    }
    if (
      llmHelper.critiqueText?.length &&
      !_.some(response, (r: any) => (r as ChatHistory).autoCritique === llmHelper.critiqueText)
    ) {
      response.push({ autoCritique: llmHelper.critiqueText, interactions: [] });
    }

    let annotations: Record<string, TextCollectionAnnotation>;
    if (response[0]?.interactions.length === 0 && chatbotContext.enabled) {
      // if there are no interactions, remove all pre-existing annotations
      annotations = {
        rlhf_history: {
          type: TextCollectionFieldType.RLHFHistory,
          field_id: 'rlhf_history',
          response,
        },
      };
    } else {
      annotations = {
        ...existingAnnotations,

        rlhf_history: {
          type: TextCollectionFieldType.RLHFHistory,
          field_id: 'rlhf_history',
          response,
        },
      };
    }
    if (!disabled) {
      setAnnotations(annotations);
    }
  }, [
    chatbotContext.history,
    setAnnotations,
    props.annotationsRef,
    props.breadcrumbs,
    disabled,
    chatbotContext.autoSpecText,
    chatbotContext.critiqueText,
    chatbotContext.enabled,
    llmHelper.enabled,
    llmHelper.autoSpecText,
    llmHelper.critiqueText,
  ]);

  const [readOnlyFieldIds, setReadOnlyFieldIds] = useState<string[]>(props.readOnlyFieldIds ?? []);

  // disable ranking field if it is a reviewer who does not want to start a new 'branch' in the history
  useEffect(() => {
    if (annotations && chatbotContext.enabled) {
      const length = chatbotContext.history.interactions.length;
      const readOnlyIds: string[] = [];
      for (let i = 0; i < length; i++) {
        readOnlyIds.push(`ranking_${i + 1}`);
      }

      if (chatbotContext.reviewNoBranching) {
        const newReadOnlyFieldIds = [...readOnlyFieldIds, ...readOnlyIds];
        setReadOnlyFieldIds(newReadOnlyFieldIds);
      } else {
        const newReadOnlyFieldIds = readOnlyFieldIds.filter(
          fieldId => !readOnlyIds.includes(fieldId),
        );
        setReadOnlyFieldIds(newReadOnlyFieldIds);
      }
    }
  }, [
    annotations,
    chatbotContext.enabled,
    chatbotContext.reviewNoBranching,
    chatbotContext.history.interactions.length,
  ]);

  // auto populate the edit completion form
  useEffect(() => {
    if (!chatbotContext.enabled) return;
    if (chatbotContext.candidateBestCompletion && props.rlhfParams?.editBestCompletion) {
      if (
        props.rlhfParams?.chatApiMultiEndpoints &&
        props.rlhfParams?.chatApiMultiEndpoints.length > 0 &&
        !chatbotContext.shouldUpdateBestCompletion
      ) {
        // early exit if ranking has not changed
        return;
      } else {
        chatbotContext.setShouldUpdateBestCompletion(false);
      }

      const existingAnnotations = getCurrentAnnotations(
        props.annotationsRef.current,
        props.breadcrumbs,
      ).annotations;

      const index = chatbotContext.history.interactions.length;

      const annotations: Record<string, TextCollectionAnnotation> = {
        ...existingAnnotations,
        [`editCompletion_${index}`]: {
          type: TextCollectionFieldType.Text,
          field_id: `editCompletion_${index}`,
          response: [chatbotContext.candidateBestCompletion],
        },
      };

      if (!disabled) {
        setAnnotations(annotations);
      }
    }
  }, [
    chatbotContext.history.interactions.length,
    chatbotContext.candidateBestCompletion,
    setAnnotations,
    props.annotationsRef,
    props.breadcrumbs,
    disabled,
  ]);

  useEffect(() => {
    if (!chatbotContext.enabled) return;
    if (
      chatbotContext.candidateEditedPrompt &&
      ((!chatbotContext.useChatbotV2 &&
        chatbotContext.reviewNoBranching &&
        chatbotContext.interactionNumber !== undefined) ||
        (chatbotContext.useChatbotV2 &&
          chatbotContext.isMakingChanges &&
          chatbotContext.selectedInteraction !== undefined))
    ) {
      const existingAnnotations = getCurrentAnnotations(
        props.annotationsRef.current,
        props.breadcrumbs,
      ).annotations;

      const index = chatbotContext.useChatbotV2
        ? chatbotContext.selectedInteraction + 1
        : chatbotContext.interactionNumber !== undefined
        ? chatbotContext.interactionNumber + 1
        : 0;

      const annotations: Record<string, TextCollectionAnnotation> = {
        ...existingAnnotations,
        [`editPrompt_${index}`]: {
          type: TextCollectionFieldType.Text,
          field_id: `editPrompt_${index}`,
          response: [chatbotContext.candidateEditedPrompt],
        },
      };

      if (!disabled) {
        setAnnotations(annotations);
      }
    }
  }, [
    chatbotContext.candidateEditedPrompt,
    chatbotContext.reviewNoBranching,
    chatbotContext.isMakingChanges,
    setAnnotations,
    props.annotationsRef,
    props.breadcrumbs,
    disabled,
  ]);

  let answeringChatbotForm = false;
  if (props.rlhfParams) {
    if (!chatbotContext.enabled) {
      chatbotContext.setTaxonomy(
        props.fields,
        props.rlhfParams.perConversationFieldIds,
        props.rlhfParams.perTurnFieldIds,
      );
      chatbotContext.setRLHFParams(props.rlhfParams!);
      chatbotContext.setEnabled(true);
    }
    chatbotContext.setChatBoxTexts(props.rlhfParams.chatBoxTexts);
    chatbotContext.setReadOnly(props.readOnly);
    chatbotContext.setAnnotations(annotations);
    chatbotContext.setIsReview(!!props.isReview);
    props.fields = chatbotContext.fieldsToShow;
    answeringChatbotForm = props.fields.length > 0 && !props.readOnly;
  }
  if (!props.rlhfParams && (props.autoSpec || props.autoCritique)) {
    llmHelper.setEnabled(true);
    llmHelper.setAutoCritique(!!props.autoCritique);
    llmHelper.setAutoSpec(!!props.autoSpec);
    llmHelper.setAnnotations(annotations);
  }
  if (props.aiAssistant) {
    llmHelper.setCurrentReviewLevel(props.reviewLevel);
    llmHelper.setIsReviewMode(!!props.isReview);
    llmHelper.setAiAssistantEnabled(true);
  }

  const { fields: currentFields, parentField } = getCurrentFields(props.fields, props.breadcrumbs);
  const [isFieldsLoaded, setIsFieldsLoaded] = React.useState(false);
  const [sbk, toggleSbk] = useToggle(false);
  const [previousResponses, setPreviousResponses] = React.useState<
    TextCollectionResponse[] | undefined
  >(undefined);

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [disabledFieldIds, setDisabledFieldIds] = useState<string[]>([]);
  const useTextCollectionShortcuts = props.textCollectionConfig?.useTextCollectionShortcuts;

  useSequentialNumericShortcuts((idx: number) => {
    setFocusedIndex(idx);
    toggleSbk();
  }, !useTextCollectionShortcuts);

  const focusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    } else {
      setFocusedIndex(-1);
    }
  }, [focusedIndex, sbk]);

  const hideFieldsWithAgreement = useUnleashBoolean(
    'tc-review-hide-agreement',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const useDolphinGenerateResponsesButton = useUnleashBoolean(
    'dolphin-custom-fields',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const useDolphinGeneratePromptButton = useUnleashBoolean(
    'dolphin-generate-prompt',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const npovRewriteOpen = useUnleashBoolean(
    'npov-rewrite-open',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  useAsync(async () => {
    if (
      props.isReview &&
      props.textCollectionConfig?.showPreviousResponsesForTextFields &&
      props.assignmentId
    ) {
      const { success, responses } = await apiFetch<TextCollectionGetPreviousResponsesResponse>(
        null,
        `internal/textcollection/${props.assignmentId}/get_previous_responses`,
        {
          method: 'GET',
        },
      );
      if (success && responses) {
        setPreviousResponses(responses);
      }
    }
  }, [props.reviewMode, props.textCollectionConfig?.showPreviousResponsesForTextFields]);

  React.useEffect(() => {
    if (
      // `disabled` is true in reviews if the tasker is not in "making changes" state, aka
      // if the tasker doesn't make changes in reviews, don't add any loaded fields.
      // This means that the review UI will initially never contain decision tree fields,
      // since the responses to those fields are always removed when attempters submit.
      !disabled &&
      props.decisionTreeFields?.length &&
      !_.some(currentFields, field => field.loaded_remotely)
    ) {
      currentFields.push(...props.decisionTreeFields);
      setIsFieldsLoaded(true);
    }
  }, [
    disabled,
    props.decisionTreeFields,
    props.decisionTreeFields?.length,
    currentFields,
    isFieldsLoaded,
  ]);

  const scrollRef: React.RefObject<HTMLDivElement> = React.useRef(null);

  // Scroll to top after clicking into fieldset or navigating to next form.
  useEffect(() => {
    if (
      props.previousBreadcrumbs &&
      props.breadcrumbs.length >= props.previousBreadcrumbs.length &&
      scrollRef.current
    ) {
      scrollRef.current.scrollTop = 0;
    }
  }, [props.breadcrumbs, props.previousBreadcrumbs]);

  const onFieldChange = React.useCallback(
    (field: TextCollectionField) => {
      const setAnnotation = function (annotation: TextCollectionAnnotation): void {
        const existingAnnotations = getCurrentAnnotations(
          props.annotationsRef.current,
          props.breadcrumbs,
        ).annotations;
        const annotations: Record<string, TextCollectionAnnotation> = {
          ...existingAnnotations,
          [field.field_id]: annotation,
        };
        if (!disabled) {
          setAnnotations(annotations);
        }
      };
      return setAnnotation;
    },
    [setAnnotations, props.annotationsRef, props.breadcrumbs, disabled],
  );

  const onSingleFieldChange = React.useCallback(
    (field: TextCollectionField) => {
      const setAnnotation = function (annotation: TextCollectionAnnotation): void {
        if (!disabled) {
          currentAnnotations[field.field_id] = annotation;
        }
      };
      return setAnnotation;
    },
    [currentAnnotations, disabled],
  );

  const setAnnotationIndex = React.useCallback(
    function (
      field: TextCollectionField,
      response: TextCollectionAnnotationsOrCallback,
      index: number,
    ): void {
      if (!disabled) {
        setAnnotations(response, [...props.breadcrumbs, { id: field.field_id, index }]);
      }
    },
    [setAnnotations, props.breadcrumbs, disabled],
  );
  const deleteAnnotationIndex = React.useCallback(
    function (field: TextCollectionField, index: number): void {
      if (!disabled) {
        props.deleteAnnotations([...props.breadcrumbs, { id: field.field_id, index }]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.deleteAnnotations, props.breadcrumbs, disabled],
  );
  const pushAnnotations = React.useCallback(
    function (
      field: TextCollectionField,
      newAnnotations: Record<string, TextCollectionAnnotation>,
    ): void {
      if (!disabled && field.type === TextCollectionFieldType.FieldSet) {
        const response = (
          (currentAnnotations[field.field_id] as FieldSetAnnotation)?.response || []
        ).slice();
        const uuids = (
          (currentAnnotations[field.field_id] as FieldSetAnnotation)?.uuids || []
        ).slice();
        uuids.push(uuid.v4());
        const currentAnnotation = currentAnnotations[field.field_id] as FieldSetAnnotation;
        const childAnnotation: FieldSetAnnotation = {
          ...currentAnnotation,
          response: [...response, newAnnotations],
          uuids,
        };
        const updatedAnnotations: Record<string, TextCollectionAnnotation> = {
          ...currentAnnotations,
          [field.field_id]: childAnnotation,
        };
        setAnnotations(updatedAnnotations, props.breadcrumbs);
      }
    },
    [setAnnotations, currentAnnotations, props.breadcrumbs, disabled],
  );
  const currentTitle = getBreadcrumbTitle(props.fields, annotations, props.breadcrumbs);
  const addBreadcrumb = React.useCallback(
    (breadcrumb: Breadcrumb): void => {
      props.setBreadcrumbs(props.breadcrumbs.concat([breadcrumb]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.setBreadcrumbs, props.breadcrumbs],
  );
  const onDelete = () => {
    const message = getDeleteMessage(props.fields, annotations, props.breadcrumbs);
    if (window.confirm(message)) {
      props.onRequestDelete();
    }
  };
  const onBreadcrumbClick = (index: number) => {
    props.setBreadcrumbs(props.breadcrumbs.slice(0, index + 1));
  };

  const onDoneClick = () => {
    props.dispatch({
      type: 'SELECT-MODE',
      payload: {
        mode: 'reviewMode',
      },
    });
  };

  const handleValidateField = async (annotation: TextCollectionAnnotation) => {
    SAlert.info(`Validating ${fieldIdPendingValidation ?? 'Current'} field`);
    const lintPassed =
      !fieldValidationEnabled ||
      !_.isString(annotation?.response?.[0]) ||
      (await validateField(annotation.response[0]));
    if (lintPassed) {
      SAlert.success(`${fieldIdPendingValidation ?? 'Current'} field passed validation`);
      setDisabledFieldIds([]);
      setFieldIdPendingValidation(undefined);
    }
  };

  const handleDisableOtherFields = React.useCallback(
    (fieldId: string) => {
      setDisabledFieldIds(currentFields.filter(f => f.field_id !== fieldId).map(f => f.field_id));
    },
    [currentFields],
  );

  // Mark field pending verification if missing response
  useEffect(() => {
    if (!fieldValidationEnabled || !!fieldIdPendingValidation) return;
    for (const field of currentFields) {
      if (
        fieldIdsToValidate.includes(field.field_id) &&
        !annotations[field.field_id]?.response?.[0]
      ) {
        setFieldIdPendingValidation(field.field_id);
        handleDisableOtherFields(field.field_id);
        break;
      }
    }
  }, [
    fieldValidationEnabled,
    fieldIdsToValidate,
    annotations,
    currentFields,
    fieldIdPendingValidation,
    setFieldIdPendingValidation,
    handleDisableOtherFields,
  ]);

  // Block other fields when a field is pending validation
  useEffect(() => {
    if (fieldIdPendingValidation) {
      handleDisableOtherFields(fieldIdPendingValidation);
    }
  }, [fieldIdPendingValidation, handleDisableOtherFields]);

  const navItems: React.ReactNode[] = [];
  // Include a link back to the top level form.
  if (props.breadcrumbs.length > 0 && props.title) {
    const onClick = () => {
      onBreadcrumbClick(-1);
    };
    navItems.push(
      <BreadcrumbLink key="top level link" onClick={onClick}>
        {props.title}
      </BreadcrumbLink>,
    );
  }
  for (let i = 0; i < props.breadcrumbs.length; i++) {
    if (navItems.length > 0) {
      navItems.push(
        <div key={`${i} slash`} className={styles.navSep}>
          /
        </div>,
      );
    }
    const title = getBreadcrumbTitle(props.fields, annotations, props.breadcrumbs.slice(0, i + 1));
    const onClick = () => {
      onBreadcrumbClick(i);
    };
    navItems.push(
      <BreadcrumbLink key={`${i} link`} onClick={onClick}>
        {title}
      </BreadcrumbLink>,
    );
  }

  const previouslyVisitedChild =
    props.previousBreadcrumbs?.length === props.breadcrumbs.length + 1 &&
    _.last(props.previousBreadcrumbs);

  const unitResponsesByField: Record<string, UnitFieldAnnotation> = {};
  const relevantFieldIds = _.filter(
    currentFields.map(field =>
      field.type === TextCollectionFieldType.TimeRange ? field.default_from_field : undefined,
    ),
  );
  _.entries(currentAnnotations).map(([key, annotation]) => {
    if (isUnitFieldAnnotation(annotation) && relevantFieldIds.includes(key)) {
      unitResponsesByField[key] = annotation;
    }
  });

  const { getFieldSuggestions } = useSuggestions();

  const setAnnotationFunctions = React.useMemo(() => {
    const fns: Record<string, (annotation: TextCollectionAnnotation) => void> = {};
    for (const field of currentFields) {
      fns[field.field_id] = onFieldChange(field);
    }
    return fns;
  }, [onFieldChange, currentFields, isFieldsLoaded]);

  const setSingleAnnotationFunctions = React.useMemo(() => {
    const fns: Record<string, (annotation: TextCollectionAnnotation) => void> = {};
    for (const field of currentFields) {
      fns[field.field_id] = onSingleFieldChange(field);
    }
    return fns;
  }, [onSingleFieldChange, currentFields]);

  useEffect(() => {
    const missingFields = currentFields.filter(field => !currentAnnotations[field.field_id]);
    if (missingFields.length) {
      const newAnnotations = {
        ...currentAnnotations,
      };
      for (const field of missingFields) {
        newAnnotations[field.field_id] = getDefaultAnnotation(field);
      }
      setAnnotations(newAnnotations);
    }
  }, [currentAnnotations, setAnnotations, currentFields, isFieldsLoaded]);

  const groupIssuesById = (diagnostics: LintDiagnostic[]) => {
    const flattenedIssues = _.reduce(
      diagnostics,
      (result: LintIssue[], diagnostic: LintDiagnostic) => {
        return [...result, ...(diagnostic.data.taskComponentAnnotations || [])];
      },
      [],
    );
    return _.groupBy(flattenedIssues, 'id');
  };

  const errors = _.filter(props.lintErrors, e => e.severity === LintSeverity.ERROR && !e.dismissed);
  const warnings = _.filter(
    props.lintErrors,
    e => e.severity !== LintSeverity.ERROR && !e.dismissed,
  );

  const errorsById = groupIssuesById(errors);
  const warningsById = groupIssuesById(warnings);

  const inlineAttachmentSections = props.inlineAttachments?.map((attachment, i) => {
    const { completedAttachments, updateCompletedAttachments } = props;
    return (
      <InlineFormAttachmentView
        attachment={attachment}
        key={i}
        markAttachmentAsCompleted={(isComplete: boolean) => {
          if (completedAttachments && updateCompletedAttachments) {
            const newAttachments = [...completedAttachments];
            newAttachments[i] = isComplete;
            updateCompletedAttachments(newAttachments);
          }
        }}
        completed={!_.isUndefined(completedAttachments) && completedAttachments[i]}
      />
    );
  });

  // hides fields that already have agreement
  // This was built for Tractable but can potentially be used for other projects.
  const fieldsToHideDueToAgreement: string[] = useMemo(() => {
    if (!hideFieldsWithAgreement || !props.agreementScores) {
      return [];
    }
    // we want to unhide every field that doesn't have an agreement score of 1, or that contains such a field as a descendant

    const needToShowField: Record<string, boolean> = {};

    // reveal fields with disagreement
    const shownFields = Object.entries(props.agreementScores)
      .filter(([_fieldName, scores]) =>
        Object.values(scores as number[]).some(score => score !== 1),
      )
      .map(([fieldName, _scores]) => fieldName);
    shownFields.forEach(fieldToShow => {
      // show the current field, and continue showing ancestors
      let currentField: string | undefined = fieldToShow;
      // optimization: don't revisit ancestors if we've already marked them up to the root
      while (currentField !== undefined && !needToShowField[currentField]) {
        needToShowField[currentField] = true;
        const fieldObject = currentFields.find(field => field.field_id === currentField);
        currentField = fieldObject?.conditions?.length
          ? Object.keys(fieldObject.conditions[0])[0]
          : undefined;
      }
    });
    return currentFields
      .filter(field => !needToShowField[field.field_id])
      .map(field => field.field_id);
  }, [hideFieldsWithAgreement, currentFields, props.agreementScores]);

  const getPrefillKey = (field: TextCollectionField): string | undefined => {
    return field.type === TextCollectionFieldType.Text &&
      field.prefill?.fieldKey &&
      field.prefill.fieldKey in currentAnnotations
      ? (currentAnnotations[field.prefill.fieldKey].response[0] as string)
      : undefined;
  };

  const diffFieldSections =
    chatbotContext.enabled && props.rlhfParams?.editBestCompletion && chatbotContext.readOnly
      ? currentFields
          .filter(
            field =>
              field.type === TextCollectionFieldType.Text &&
              field.field_id.startsWith('editCompletion_'),
          )
          .map(field => {
            if (field.type !== TextCollectionFieldType.Text) return null;
            const interactionIndexStr = field.field_id.substring(15); // editCompletion_{number} is the field id
            const originalCompletionIndex = _.get(
              currentAnnotations,
              `ranking_${interactionIndexStr}.response[0]`,
              '0',
            ); // get the top ranking completion or default to index 0 if theres no ranking q (assumes 1 completion)
            const originalCompletion = _.get(
              currentAnnotations,
              `rlhf_history.response[0].interactions[${+interactionIndexStr - 1}]`,
            );
            const originalCompletionText = _.get(
              originalCompletion,
              `completions[${originalCompletionIndex}].text`,
            );
            const annotation = currentAnnotations[field.field_id]?.response[0];

            return (
              <div key={field.field_id}>
                <TextCollectionDiff
                  title={field.title}
                  originalText={originalCompletionText as string | undefined}
                  newText={annotation as string | undefined}
                />
                {field.show_latex_preview && (
                  <div>
                    <link
                      rel="stylesheet"
                      type="text/css"
                      href="//cdnjs.cloudflare.com/ajax/libs/KaTeX/0.11.1/katex.min.css"
                    />
                    <p>
                      <strong>LaTex Preview:</strong>
                    </p>
                    <Latex>{annotation as string}</Latex>
                  </div>
                )}
                {field.show_markdown_preview && (
                  <div className={styles.markdown}>
                    <p>
                      <strong>Markdown Preview:</strong>
                    </p>
                    {annotation && <MarkdownComponent markdown={annotation as string} />}
                  </div>
                )}
              </div>
            );
          })
      : currentFields
          .filter(field => field.type === TextCollectionFieldType.Text && field.show_diff)
          .map(field => {
            if (field.type !== TextCollectionFieldType.Text) return null;

            const annotation = currentAnnotations[field.field_id]?.response[0];
            return (
              <div key={field.field_id}>
                <TextCollectionDiff
                  title={field.title}
                  originalText={field.original_text}
                  newText={annotation as string | undefined}
                />
                {field.show_latex_preview && (
                  <div>
                    <link
                      rel="stylesheet"
                      type="text/css"
                      href="//cdnjs.cloudflare.com/ajax/libs/KaTeX/0.11.1/katex.min.css"
                    />
                    <p>
                      <strong>LaTex Preview:</strong>
                    </p>
                    <Latex>{annotation as string}</Latex>
                  </div>
                )}
                {field.show_markdown_preview && (
                  <div className={styles.markdown}>
                    <p>
                      <strong>Markdown Preview:</strong>
                    </p>
                    {annotation && <MarkdownComponent markdown={annotation as string} />}
                  </div>
                )}
              </div>
            );
          });

  let fieldSections = currentFields
    .filter(field => {
      if (!currentAnnotations[field.field_id]) {
        // resolves Cannot add property, object is not extensible error
        currentAnnotations = _.cloneDeep(currentAnnotations);
        currentAnnotations[field.field_id] = getDefaultAnnotation(field);
      }

      if (fieldsToHideDueToAgreement.includes(field.field_id)) return false;
      // Skip making FieldSection if the field's conditions are not met
      if (
        field.conditions &&
        !areAllFieldConditionsMet(
          field.conditions,
          flattenAnnotations(annotations),
          Object.values(flattenFields(props.fields)),
        )
      ) {
        return;
      }
      if (field.conditionsCodeStr && !isFieldConditionsCodeMet(field, props.fields, annotations)) {
        return false;
      }

      if (
        field.templateVariableConditions &&
        templateVariables &&
        !areTemplateVariableConditionsMet(field, templateVariables)
      ) {
        return false;
      }

      return true;
    })
    .map((field, idx) => {
      if (!currentAnnotations[field.field_id]) {
        currentAnnotations[field.field_id] = getDefaultAnnotation(field);
      }

      const annotation = currentAnnotations[field.field_id];
      const setAnnotation = setAnnotationFunctions[field.field_id];
      const setSingleAnnotation = setSingleAnnotationFunctions[field.field_id];
      const quickAddResponses = props.quickAddFieldSets.includes(field.field_id)
        ? getQuickAddResponses(annotations, props.breadcrumbs, field)
        : [];
      const blurred = props.blurredFieldIds?.includes(field.field_id);
      const fieldDisabledForPrompt = disabledFieldIds?.includes(field.field_id);
      const dolphinFieldDisabled =
        (useDolphinGenerateResponsesButton &&
          [DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A, DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_B].includes(
            field.field_id,
          )) ||
        (useDolphinGeneratePromptButton && field.field_id === GENERATED_PROMPT_FIELD_ID);

      const dolphinSetEnhancedResponse = useDolphinGenerateResponsesButton
        ? {
            setEnhancedResponseFn: setAnnotationFunctions[DOLPHIN_MISC.DOLPHIN_ENHANCED_FIELD],
            generatedResponseA: currentAnnotations[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A],
            generatedResponseB: currentAnnotations[DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_B],
            hasEnhancedResponseField: !!currentFields.find(
              field => field.field_id === DOLPHIN_MISC.DOLPHIN_ENHANCED_FIELD,
            ),
          }
        : undefined;

      const codingFields = Object.values(currentAnnotations).filter(
        annotation => annotation.type === TextCollectionFieldType.Code,
      );

      return (
        <div
          key={field.field_id}
          style={blurred ? { opacity: 0.3 } : {}}
          ref={idx === focusedIndex ? focusRef : undefined}
          tabIndex={0}
        >
          {useDolphinGeneratePromptButton && field.field_id === GENERATED_PROMPT_FIELD_ID && (
            <div className={styles.dolphinComponents}>
              <GeneratePromptButton
                readOnly={props.readOnly}
                assignmentId={props.assignmentId}
                currentFields={currentFields}
                currentAnnotations={currentAnnotations}
                setAnnotationFunctions={setAnnotationFunctions}
              />
            </div>
          )}
          <FieldSection
            isTestQuestion={props.isTestQuestion}
            field={field}
            annotation={annotation}
            currentAnnotations={currentAnnotations}
            isCustomer={props.isCustomer}
            previouslyVisitedChild={previouslyVisitedChild || undefined}
            setAnnotation={setAnnotation}
            setAnnotationIndex={setAnnotationIndex}
            deleteAnnotationIndex={deleteAnnotationIndex}
            pushAnnotations={pushAnnotations}
            addBreadcrumb={addBreadcrumb}
            autoComplete={props.autoComplete}
            disabled={disabled || dolphinFieldDisabled}
            fieldDisabledForPrompt={fieldDisabledForPrompt}
            blocked={blocked}
            quickAddResponses={quickAddResponses}
            unitResponsesByField={unitResponsesByField}
            suggestions={getFieldSuggestions(field, currentAnnotations)}
            readOnlyFieldIds={readOnlyFieldIds}
            bboxFieldToEdit={props.bboxFieldToEdit}
            setBboxFieldToEdit={props.setBboxFieldToEdit}
            attachment={props.attachment}
            errors={errorsById[field.field_id]}
            warnings={warningsById[field.field_id]}
            assignmentId={props.assignmentId}
            loggedUserId={props.loggedUserId}
            textCollectionConfig={props.textCollectionConfig}
            isAudit={props.isAudit}
            prefillKey={getPrefillKey(field)}
            setSingleAnnotation={setSingleAnnotation}
            rankingOrderOptions={props.rankingOrderOptions}
            projectId={props.projectId}
            index={useTextCollectionShortcuts ? idx : undefined}
            previousFieldAnnotations={previousResponses?.map(response =>
              _.get(response.annotations, field.field_id),
            )}
            dolphinSetEnhancedResponse={dolphinSetEnhancedResponse}
            setSubmitCodeError={props.setSubmitCodeError}
            useSimpleCodeField={props.useSimpleCodeField}
            handleValidateField={
              fieldIdsToValidate.includes(field.field_id) ? handleValidateField : undefined
            }
            setFieldIdPendingValidation={setFieldIdPendingValidation}
            fieldIdPendingValidation={fieldIdPendingValidation}
            fieldValidationResult={fieldValidationResults[field.field_id]}
            fieldValidationEnabled={fieldValidationEnabled}
            codingFields={codingFields}
            logBehaviourActions={props.logBehaviourActions}
            chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
            readOnlyMessage={props.readOnlyMessage}
          />
          {useDolphinGenerateResponsesButton &&
            field.field_id === DOLPHIN_MISC.DOLPHIN_PROMPT_FIELD && (
              <div className={styles.dolphinComponents}>
                <DolphinGenerationButton
                  readOnly={props.readOnly}
                  assignmentId={props.assignmentId}
                  projectId={props.projectId}
                  currentFields={currentFields}
                  currentAnnotations={currentAnnotations}
                  setAnnotationFunctions={setAnnotationFunctions}
                />
              </div>
            )}
        </div>
      );
    });

  // TODO clean up hack for creating section headers
  if (chatbotContext.enabled) {
    fieldSections = chatbotContext.formatSections(fieldSections);
  }
  if (!chatbotContext.enabled && llmHelper.enabled) {
    fieldSections = llmHelper.formatSections(fieldSections);
  }

  const isFormField = isFormFields(_.compact([parentField]));

  return (
    <div className={styles.form}>
      {props.captcha && !props.isScaleStudio && (
        <div style={{ marginLeft: 30, marginTop: 30 }}>
          <div>Solve the following question to gain credit:</div>
          <br />
          <div>{props.captcha}</div>
          <br />
          <TextInput
            onChange={e => {
              props.setCaptchaUserAnswer && props.setCaptchaUserAnswer(e.target.value);
            }}
            rows={1}
            value={props.captchaUserAnswer}
            disabled={false}
          />
        </div>
      )}
      {props.breadcrumbs.length > 0 && !isFormField && (
        <div className={styles.nav}>
          <div className={styles.navButton}>
            {!props.reviewMode && (
              <>
                <Button
                  classes={{ contained: styles.backButton }}
                  type="button"
                  variant="contained"
                  color="default"
                  startIcon={<ArrowLeft />}
                  onClick={props.onRequestBack}
                >
                  Back
                </Button>
                <>
                  {npovRewriteOpen && (
                    <Button
                      classes={{ contained: styles.npovButton }}
                      type="button"
                      variant="contained"
                      color="default"
                      onClick={props.onRequestBack}
                    >
                      Done
                    </Button>
                  )}
                </>
              </>
            )}
            {props.reviewMode && (
              <Button
                type="button"
                variant="contained"
                color="default"
                classes={{ root: styles.backIconButton, contained: styles.backButton }}
                onClick={props.onRequestBack}
              >
                <ArrowLeft />
              </Button>
            )}
          </div>
          <div className={styles.breadcrumbs}>{navItems}</div>
          {props.reviewMode && (
            <div className={styles.navButton}>
              <Button
                classes={{ root: styles.doneButton }}
                type="button"
                variant="contained"
                color="primary"
                onClick={onDoneClick}
              >
                Done
              </Button>
            </div>
          )}
        </div>
      )}
      {!props.useCrabHack ? (
        <div className={styles.scroll} ref={scrollRef}>
          {props.breadcrumbs.length > 0 && !isFormField && (
            <div className={styles.header}>
              <h3 className={styles.headerTitle}>{currentTitle}</h3>
              {!props.readOnly && (
                <Button classes={{ root: styles.deleteButton }} variant="text" onClick={onDelete}>
                  Delete
                </Button>
              )}
            </div>
          )}
          {props.breadcrumbs.length === 0 && props.description && (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: Markdown.renderInline(props.description) }}
            />
          )}
          {parentField && isFormField && (
            <div className={styles.formField}>
              <h3
                className={styles.headerTitle}
                dangerouslySetInnerHTML={{ __html: Markdown.renderInline(parentField.title) }}
              />
              {parentField.description && (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: Markdown.renderInline(parentField.description),
                  }}
                />
              )}
            </div>
          )}
          <div className={styles.inlineAttachmentSections}>{inlineAttachmentSections}</div>
          <div className={styles.fieldList}>
            {useTextCollectionShortcuts && (
              <div style={{ paddingLeft: 8 }}>
                Press <Kbd>Tab</Kbd> to go to the next question or next input. Use <Kbd>Up</Kbd> and{' '}
                <Kbd>Down</Kbd> keys to navigate between choices
              </div>
            )}
            {!fieldSections.some(section => !!section) && fieldsToHideDueToAgreement.length > 0 ? (
              <div>No fields to check. Please approve!</div>
            ) : (
              fieldSections
            )}
            {diffFieldSections}
            {(chatbotContext.autoSpecLoading || llmHelper.autoSpecLoading) && (
              <div style={{ padding: '0 8px' }}>Waiting for your ranking guidelines...</div>
            )}
          </div>

          <div className={styles.fieldList}>
            {answeringChatbotForm &&
              !chatbotContext.isDone &&
              !chatbotContext.useChatbotV2 &&
              !chatbotContext.hideNextButton && (
                <div data-for={`rlhf-next-button-tip`} data-tip={chatbotContext.nextButtonDisabled}>
                  <Button
                    id="rlhf-next-button"
                    classes={{ contained: styles.doneButton }}
                    type="button"
                    variant="contained"
                    color="default"
                    onClick={() => {
                      (async () => {
                        const noErrors = props.perTurnCheck ? await props.perTurnCheck() : true;
                        noErrors && chatbotContext.submitChatbotForm();
                      })();
                    }}
                    disabled={!!chatbotContext.nextButtonDisabled}
                  >
                    Next
                  </Button>
                  {chatbotContext.nextButtonDisabled && (
                    <ReactTooltip place="top" id="rlhf-next-button-tip" />
                  )}
                </div>
              )}

            {!chatbotContext.isDone &&
              !answeringChatbotForm &&
              chatbotContext.rangedMultiTurnCanSubmit && (
                <div>
                  <Button
                    id="rlhf-end-conversation-button"
                    type="button"
                    variant="contained"
                    classes={{ root: styles.continueButton, contained: styles.continueButton }}
                    onClick={() => chatbotContext.rangedMultiTurnEndConversation()}
                  >
                    End Conversation Now
                  </Button>
                </div>
              )}

            {chatbotContext.useChatbotV2 &&
              answeringChatbotForm &&
              !chatbotContext.hideNextButton &&
              !chatbotContext.onPerConvoField && (
                <div data-for={`rlhf-next-button-tip`} data-tip={chatbotContext.nextButtonDisabled}>
                  <Button
                    id="rlhf-next-button"
                    type="button"
                    variant="contained"
                    classes={{ root: styles.continueButton, contained: styles.continueButton }}
                    onClick={() => {
                      (async () => {
                        await chatbotContext.submitChatbotFormAsync();
                      })();
                    }}
                    disabled={!!chatbotContext.nextButtonDisabled}
                  >
                    Save & Continue Task
                  </Button>
                  {chatbotContext.nextButtonDisabled && (
                    <ReactTooltip place="top" id="rlhf-next-button-tip" />
                  )}
                </div>
              )}
          </div>
        </div>
      ) : (
        <div className={styles.crabHackColumns}>
          <div
            className={styles.fieldList}
            style={{ width: '60%', height: '100%', overflowY: 'scroll' }}
          >
            {diffFieldSections && diffFieldSections.length > 0
              ? diffFieldSections
              : fieldSections.slice(0, numLeftColumnFields)}
          </div>
          <div className={styles.fieldList} style={{ width: '40%', overflowY: 'scroll' }}>
            {fieldSections.filter(
              (a, idx) =>
                idx >
                (diffFieldSections && diffFieldSections.length > 0 ? -1 : numLeftColumnFields - 1),
            )}
          </div>
        </div>
      )}
      {parentField && isFormField && !props.omitFormFieldNav && isFormFields(props.fields) && (
        <FormFieldNav
          formFields={props.fields}
          activeFieldId={parentField.field_id}
          setBreadcrumbs={props.setBreadcrumbs}
          onNavigate={props.onFormFieldNavigate}
        />
      )}
    </div>
  );
}

interface BreadcrumbLinkProps {
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<any>) => void;
  children?: React.ReactNode;
}

const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = function (props) {
  const styles = useTextCollectionFormStyles(props);
  if (props.disabled) {
    return <div className={styles.navLinkDisabled}>{props.children}</div>;
  } else {
    return (
      <div className={styles.navLinkContainer}>
        <Link
          classes={{ root: styles.navLink }}
          color="inherit"
          component="div"
          underline="none"
          onClick={props.onClick as $FIXME}
        >
          {props.children}
        </Link>
      </div>
    );
  }
};

function ArrowLeft() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.256 19.0919L12.4235 19.9244C12.071 20.2769 11.501 20.2769 11.1523 19.9244L3.86228 12.6381C3.50978 12.2856 3.50978 11.7156 3.86228 11.3669L11.1523 4.07687C11.5048 3.72438 12.0748 3.72438 12.4235 4.07687L13.256 4.90937C13.6123 5.26562 13.6048 5.84687 13.241 6.19562L8.72229 10.5006H19.4998C19.9986 10.5006 20.3998 10.9019 20.3998 11.4006V12.6006C20.3998 13.0994 19.9986 13.5006 19.4998 13.5006H8.72229L13.241 17.8056C13.6085 18.1544 13.616 18.7356 13.256 19.0919Z"
        fill="black"
      />
    </svg>
  );
}

const darken = (themeColor: string) => color(themeColor).darken(0.1).toString();

const useTextCollectionFormStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    height: '100%',
    background: theme.colors.white,
  },
  description: {
    fontWeight: 500,
    color: '#000000',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '16px',
    marginTop: '8px',
  },
  nav: {
    padding: '16px',
    display: 'flex',
    marginBottom: 32 - 24,
    flexDirection: 'row',
    flexShrink: 0,
    borderBottom: '1px solid ' + theme.colors.gray10,
    alignItems: 'center',
  },
  navButton: {
    flexShrink: 0,
    height: '100%',
    marginRight: 8,
  },
  breadcrumbs: {
    paddingLeft: '16px',
    paddingRight: '16px',
    marginRight: 'auto',
  },
  navSep: {
    display: 'inline-block',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontSize: '13px',
    lineHeight: '20px',
  },
  navLink: {
    display: 'inline-block',
    cursor: 'pointer',
    color: '#808080',
    fontSize: '13px',
    lineHeight: '20px',
    '&:hover': {
      color: '#000000',
    },
  },
  navLinkContainer: {
    maxHeight: 100,
    overflowY: 'scroll',
  },
  navLinkDisabled: {
    display: 'inline-block',
    color: '#808080',
    fontSize: '13px',
    lineHeight: '20px',
    maxHeight: 100,
    overflowY: 'scroll',
  },
  breadcrumbText: {
    display: 'inline-block',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '20px',
    color: `#808080`,
  },
  scroll: {
    flexShrink: 1,
    overflowY: 'scroll',
  },
  inlineAttachmentSections: {
    margin: 8,
    '& > *': {
      marginBottom: 4,
    },
  },
  dolphinComponents: {
    margin: 8,
  },
  fieldList: {
    padding: 8,
  },
  backButton: {
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    ltterSpacing: '0.1px',
    textTransform: 'none',
    backgroundColor: '#E6E6E6',
  },
  backIconButton: {
    minWidth: 0,
  },
  doneButton: {
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    ltterSpacing: '0.1px',
    textTransform: 'none',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 500,
  },
  deleteButton: {
    color: '#EA5F28',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    border: '1px solid #CCC',
    marginRight: 8,
  },
  npovButton: {
    marginLeft: '4px',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    ltterSpacing: '0.1px',
    textTransform: 'none',
    backgroundColor: '#2773ff',
    color: 'white',
  },
  formField: {
    paddingLeft: 16,
    paddingRight: 32,
  },
  continueButton: {
    background: '#6A5DF9',
    color: theme.colors.white,
    '&:hover, &:focus': {
      background: darken('#6A5DF9'),
    },
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    textTransform: 'none',
    margin: '15px',
  },
  crabHackColumns: {
    display: 'flex',
    flexDirection: 'row',
    overflowY: 'scroll',
    height: '100%',
  },
  markdown: {
    lineHeight: 0,
    '& table, & pre, & h1, & h2, & h3, & h4, & h5': {
      lineHeight: 1,
    },
  },
}));
